/**
 * Custom fields (PRD v2 §2.2) — the per-shop questions every FSM grows a junk drawer for.
 * A leaf module: it takes the idempotency envelope from the spine and nothing imports back.
 */
import { z } from 'zod';
import { toSlugKey } from '../slug';
import { idempotency } from './spine';

/** Commercial Job fields are separate from existing execution Work Order fields. */
export const CUSTOM_FIELD_TARGETS = [
  'customer',
  'property',
  'job',
  'commercial_job',
  'quote',
  'invoice',
] as const;
export type CustomFieldTarget = (typeof CUSTOM_FIELD_TARGETS)[number];

/**
 * Five kinds. Not a type system — the answer to "what does a home-service shop actually
 * ask": a bit of text, a quantity, a date, a yes/no, a pick-from-list.
 */
export const CUSTOM_FIELD_KINDS = [
  'text',
  'number',
  'date',
  'checkbox',
  'select',
] as const;
export type CustomFieldKind = (typeof CUSTOM_FIELD_KINDS)[number];

/**
 * The stable identity of a field. Derived from the label once and then FROZEN, so renaming
 * "Gate code" to "Access code" doesn't orphan every answer already recorded against it.
 *
 * The rule itself is `toSlugKey`, which service types share; this name stays for the
 * custom-field callers.
 */
export const toFieldKey = toSlugKey;

const label = z.string().trim().min(1).max(80);
const options = z.array(z.string().trim().min(1).max(80)).min(1).max(50);

export const customFieldCreateInput = idempotency
  .extend({
    entityType: z.enum(CUSTOM_FIELD_TARGETS),
    label,
    kind: z.enum(CUSTOM_FIELD_KINDS),
    options: options.optional(),
    helpText: z.string().trim().max(300).optional().or(z.literal('')),
    required: z.boolean().optional(),
    showOnTech: z.boolean().optional(),
  })
  // A dropdown with no options is a field nobody can answer; options on a date field is a
  // sign somebody picked the wrong kind.
  .refine((v) => v.kind !== 'select' || (v.options?.length ?? 0) > 0, {
    message: 'A dropdown needs at least one choice',
    path: ['options'],
  })
  .refine((v) => v.kind === 'select' || !v.options, {
    message: 'Only a dropdown has choices',
    path: ['options'],
  });
export type CustomFieldCreateInput = z.infer<typeof customFieldCreateInput>;

export const customFieldUpdateInput = idempotency.extend({
  definitionId: z.string().uuid(),
  label: label.optional(),
  options: options.optional(),
  helpText: z.string().trim().max(300).optional().or(z.literal('')),
  required: z.boolean().optional(),
  showOnTech: z.boolean().optional(),
  sortOrder: z.coerce.number().int().min(0).max(999).optional(),
  active: z.boolean().optional(),
});
export type CustomFieldUpdateInput = z.infer<typeof customFieldUpdateInput>;

export const customFieldIdInput = idempotency.extend({
  definitionId: z.string().uuid(),
});
export type CustomFieldIdInput = z.infer<typeof customFieldIdInput>;

/**
 * Setting an answer. ONE input for all five kinds, carrying the raw string the control
 * produced — the server knows the field's kind and does the conversion, so a client can
 * never disagree with the database about what type an answer is.
 *
 * `null` CLEARS the answer. That has to be as easy as setting one, or every optional field
 * fills with blanks nobody meant.
 */
export const customFieldSetInput = idempotency.extend({
  definitionId: z.string().uuid(),
  entityId: z.string().uuid(),
  value: z.string().nullable(),
});
export type CustomFieldSetInput = z.infer<typeof customFieldSetInput>;

/** A definition plus this record's answer — what a detail page renders. */
export interface CustomFieldAnswer {
  definitionId: string;
  fieldKey: string;
  label: string;
  kind: CustomFieldKind;
  options: string[] | null;
  helpText: string | null;
  required: boolean;
  showOnTech: boolean;
  sortOrder: number;
  /** The answer as a string, whichever column it came out of. Null = unanswered. */
  value: string | null;
}

/** Render an answer for a human. `—` for unanswered, never a blank cell (data-honesty). */
export function formatCustomValue(answer: CustomFieldAnswer): string {
  if (answer.value === null || answer.value === '') return '—';
  if (answer.kind === 'checkbox') return answer.value === 'true' ? 'Yes' : 'No';
  return answer.value;
}
